import { IHierarchyNode, IHierarchyService } from "../../../hierarchy/common/hierarchy.js";
import { IInstantiationService } from "../../../instantiation/common/instantiation.js";
import { ILogService } from "../../../log/common/log.js";
import { IProjectService } from "../../../../editor/services/project/common/project.js";
import { IObjectTargetService } from "../../../../editor/services/project/common/documentTarget.js";
import { aiTargetFor, type AiTarget, type TargetResolverLike } from "../target.js";
// cycle-safe: this text goes out with EVERY message — a throw here costs the
// model the whole scene, and a live object that loops is one field away
import { IResourceService } from "../../../resources/common/resources.js";
import { displayComponentName } from "../../../webgl/three.js/componentName.js";
import { safeStringify } from "../../../../base/common/objects.js";
import { AIContextProviderCapabilities, AITextContextChunk, IAIContextProvider } from "./../ai.js";


/**
 * `Type 3, Other 1` — or, for a labelled group with nothing in it, an explicit
 * "no X". Saying NOTHING would leave the model to find out with a tool call,
 * which is the round trip this whole line exists to save.
 */
function byCount(counts: ReadonlyMap<string, number>, label: string | null): string {
    if (counts.size === 0) return label ? `No ${label} in this scene.` : "";
    const sorted = [...counts.entries()].sort((a, b) => b[1] - a[1]).slice(0, 15);
    const body = sorted.map(([name, count]) => `${name} ${count}`).join(", ");
    return label ? `${label[0].toUpperCase()}${label.slice(1)}: ${body}.` : body;
}

export class AIHierarchyContext implements IAIContextProvider {
    readonly name = "scene-hierarchy";
    readonly capabilities: AIContextProviderCapabilities = AIContextProviderCapabilities.Text;
    /** anchored bubbles don't get the overview (Marcel 2026-08-25): the anchor
     *  is the subject, the overview is a per-message token tax, and the model
     *  can still call hierarchy_search_nodes to reach out */
    readonly omitForAnchoredChats = true;

    constructor(
        @IHierarchyService private readonly hierarchy: IHierarchyService,
        @ILogService private readonly logger: ILogService,
        @IInstantiationService private readonly instantiation: IInstantiationService,
    ) {
    }

    /** lazily resolved — absent outside the document products (chrome), where
     *  the target seam degrades to ref-less graph addresses */
    private get targets(): TargetResolverLike | undefined {
        try {
            return this.instantiation.invokeFunction(accessor => accessor.get(IObjectTargetService)) as TargetResolverLike;
        } catch {
            return undefined;
        }
    }

    /** the document target for a node's owner, or undefined (never a session id) */
    private mint(node: IHierarchyNode): AiTarget | undefined {
        const owner = node.owner;
        if (!owner || typeof owner !== "object") return undefined;
        return aiTargetFor(this.targets, this.hierarchy, owner) ?? undefined;
    }

    async getContext(): Promise<AITextContextChunk[] | null> {

        if (!this.hierarchy) {
            this.logger.warn("AIAppContext: No hierarchy available.");
            return null;
        }

        const hierarchy = this.hierarchy;

        // A CENSUS + the top level, not a tree (Marcel 2026-08-25). This is
        // re-sent with every chat message, so its size is a per-message tax —
        // it used to carry up to 300 nodes (no search tool existed), then a
        // 40-node/depth-3 excerpt whose per-node target JSON was most of the
        // bytes. "How many nodes, what types" answers "what is this scene"
        // better than three levels of listings; everything deeper is one
        // hierarchy_search_nodes call away.
        const census = this.collectCensus(hierarchy.roots);
        let text = `Scene overview — ${census.total} nodes (editor-helper internals excluded). By type: ${census.line}.\n`
            // WHAT THE SCENE HOLDS, stated rather than searched for: the model
            // spent two rounds on component_search discovering that a plain
            // three.js page has no components at all (2026-08-26). "None" is the
            // single most useful thing this line can say.
            + `${census.components}\n`
            + this.resourceLine()
            + "Only the TOP LEVEL is listed — call hierarchy_search_nodes to find anything else, then pass a `target` verbatim to the property tools:\n";
        const listed = new Set<string>();
        for (const node of hierarchy.roots) {
            text += safeStringify(await this.serializeHierarchy({}, node, { nodes: 0, maxNodes: AIHierarchyContext.MAX_OVERVIEW_NODES }, 0, listed));
        }

        // Cameras and lights are asked about constantly and are usually buried far
        // below the overview's depth. A handful of lines here saves a search round
        // trip on nearly every conversation.
        const notable = this.collectNotable(hierarchy.roots, listed);
        if (notable.length) {
            text += `\nNotable objects (found anywhere in the tree):\n${safeStringify(notable)}`;
        }

        return [{
            type: 'text',
            text: text,
        }]

    }


    /**
     * What the RESOURCE REGISTRY holds, by kind.
     *
     * Not derived from a scene walk: the registry is the authority, and it also
     * knows about resources nothing references yet (a loaded asset, a material
     * the user made) which no walk can see. Products without the service — the
     * chrome extension — print nothing rather than an empty line, since this
     * text is re-sent with every message.
     */
    private resourceLine(): string {
        const counts = this.instantiation.invokeFunction(accessor =>
            accessor.getIfExists(IResourceService)?.counts());
        if (!counts) return "";
        const alive = [...counts.entries()]
            .map(([kind, count]) => [kind, count.alive] as const)
            .filter(([, n]) => n > 0)
            .sort((a, b) => b[1] - a[1]);
        if (!alive.length) return "Resources: none loaded.\n";
        return `Resources: ${alive.map(([kind, n]) => `${kind} ${n}`).join(", ")}.\n`;
    }

    /** Editor-manipulation helpers whose INTERNAL nodes are pure noise for the
     *  model: a single TransformControls contributes ~50 X/Y/Z handle meshes,
     *  and three of them once dominated the whole overview (10k chars of gizmo
     *  junk). Collapse them to one leaf — the type name says everything. */
    private static readonly COLLAPSED_HELPER_TYPES = /^TransformControls(Gizmo|Plane|Root)?$/;

    /** the whole overview budget — cheap enough to re-send every message */
    private static readonly MAX_OVERVIEW_NODES = 40;
    /** the roots and their DIRECT children only — the census carries the rest
     *  (a top level can be wide too: MAX_OVERVIEW_CHILDREN caps the listing) */
    private static readonly MAX_OVERVIEW_DEPTH = 1;
    /** census types beyond this many collapse to "+n more types" */
    private static readonly MAX_CENSUS_TYPES = 15;

    /** every node counted by type — including everything the listing omits.
     *  Helper SUBTREES stay out (the helper node itself counts as one): fifty
     *  gizmo handle meshes must not read as scene content. */
    private collectCensus(roots: readonly IHierarchyNode[]): {
        total: number; line: string; components: string;
    } {
        const counts = new Map<string, number>();
        const components = new Map<string, number>();
        let total = 0;

        const visit = (node: IHierarchyNode) => {
            total++;
            counts.set(node.type, (counts.get(node.type) ?? 0) + 1);

            // components only: RESOURCES come from the registry, which already
            // indexes materials, textures and geometries — and knows about the
            // ones nothing in the scene references yet (Marcel 2026-08-26)
            const owner = node.owner as { userData?: { components?: unknown[] } } | undefined;
            for (const component of owner?.userData?.components ?? []) {
                if (!component || typeof component !== "object") continue;
                const name = displayComponentName(component);
                if (name) components.set(name, (components.get(name) ?? 0) + 1);
            }

            if (AIHierarchyContext.COLLAPSED_HELPER_TYPES.test(node.type)) return;
            for (const child of node.children) visit(child);
        };
        for (const root of roots) visit(root);

        const sorted = [...counts.entries()].sort((a, b) => b[1] - a[1]);
        const shown = sorted.slice(0, AIHierarchyContext.MAX_CENSUS_TYPES);
        const line = shown.map(([type, count]) => `${type} ${count}`).join(", ")
            + (sorted.length > shown.length ? `, +${sorted.length - shown.length} more types` : "");

        return { total, line, components: byCount(components, "components") };
    }
    /** a wide node reports its child count instead of listing them */
    private static readonly MAX_OVERVIEW_CHILDREN = 12;

    /** types worth naming wherever they sit — the questions that arrive most */
    private static readonly NOTABLE_TYPES = /Camera$|Light$/;
    /** a scene with fifty lights gets a sample, not an inventory */
    private static readonly MAX_NOTABLE = 12;

    /** notable objects NOT already in the overview (listing a thing twice teaches
     *  the model the scene has two of it) */
    private collectNotable(roots: readonly IHierarchyNode[], listed: ReadonlySet<string>): object[] {
        const found: object[] = [];
        const visit = (node: IHierarchyNode) => {
            if (found.length >= AIHierarchyContext.MAX_NOTABLE) return;
            if (AIHierarchyContext.NOTABLE_TYPES.test(node.type)) {
                const target = this.mint(node);
                if (target && !listed.has(safeStringify(target))) {
                    found.push({ target, name: node.name, type: node.type });
                }
            }
            if (AIHierarchyContext.COLLAPSED_HELPER_TYPES.test(node.type)) return;
            for (const child of node.children) visit(child);
        };
        for (const root of roots) visit(root);
        return found;
    }

    private async serializeHierarchy(object: Record<string, object | string | number | boolean>, node: IHierarchyNode, context: { nodes: number, readonly maxNodes: number }, depth: number = 0, listed?: Set<string>): Promise<Record<string, object | string | number | boolean>> {

        // Deliberately lean: name, type and the ADDRESS to act on it. Detail (properties,
        // deeper subtrees, anything not listed here) is pulled on demand via
        // hierarchy_search_nodes / property_find / property_read.
        if (context.nodes++ >= context.maxNodes) {
            object["system:warning"] = `<truncated — call hierarchy_search_nodes to find nodes not listed>`;
            return object;
        }

        if (node.name?.length) object["name"] = node.name;
        // the target is what other tools take; session handles and node ids are
        // internal and never reach the model (1h)
        const target = this.mint(node);
        if (target) { object["target"] = target; listed?.add(safeStringify(target)); }
        object["type"] = node.type;

        // Gizmo/helper subtrees: emit the node itself (id + type carry the
        // signal) but never their internals.
        if (AIHierarchyContext.COLLAPSED_HELPER_TYPES.test(node.type)) {
            return object;
        }
        if (node.children.length === 0) return object;

        // past the overview's depth or width, the COUNT is the signal — the model
        // reaches the rest through hierarchy_search_nodes, which costs nothing until asked
        if (depth >= AIHierarchyContext.MAX_OVERVIEW_DEPTH) {
            object["childCount"] = node.children.length;
            return object;
        }

        const children = object["children"] = new Array<object>();
        for (const child of node.children) {
            if (children.length >= AIHierarchyContext.MAX_OVERVIEW_CHILDREN) {
                object["childCount"] = node.children.length;
                object["system:note"] = `${node.children.length - children.length} more children — call hierarchy_search_nodes`;
                break;
            }
            if (context.nodes >= context.maxNodes) {
                break;
            }
            const childObject = {};
            await this.serializeHierarchy(childObject, child, context, depth + 1, listed);
            children.push(childObject);
        }


        return object;
    }
}
